"use client";

import { useState } from "react";
import type { Task, TaskStatus } from "@/lib/frontmatter/types";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui/components/select";
import { cn } from "@workspace/ui/lib/utils";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { SingleUserField } from "./user-chip";

const STATUS_OPTIONS: TaskStatus[] = ["open", "in-progress", "done", "wont-fix"];

interface TasksFieldProps {
  value?: Task[];
  editing?: boolean;
  onChange?: (next: Task[]) => void;
}

export function TasksField({ value, editing, onChange }: TasksFieldProps) {
  const tasks = value ?? [];

  if (!editing) {
    if (tasks.length === 0) {
      return <span className="text-sm text-muted-foreground italic">—</span>;
    }
    return (
      <ul className="space-y-1.5">
        {tasks.map((t) => (
          <li
            key={`${t.item}:${t.due ?? ""}:${t.owner ?? ""}`}
            className="flex items-start gap-2 text-sm leading-snug"
          >
            <StatusPill status={t.status} />
            <span className="flex-1 min-w-0 break-words">{t.item}</span>
            {t.owner && (
              <span className="text-muted-foreground shrink-0">@{t.owner}</span>
            )}
            {t.due && <DueLabel due={t.due} done={t.status === "done"} />}
          </li>
        ))}
      </ul>
    );
  }

  const update = (i: number, patch: Partial<Task>) => {
    const next = tasks.map((t, idx) => (idx === i ? { ...t, ...patch } : t));
    onChange?.(next);
  };

  const remove = (i: number) => {
    onChange?.(tasks.filter((_, idx) => idx !== i));
  };

  const add = () => {
    onChange?.([
      ...tasks,
      { item: "", status: "open" as TaskStatus },
    ]);
  };

  return (
    <div className="space-y-2">
      {tasks.map((t, i) => (
        <TaskEditor
          // Index is the only stable identity while items are being typed —
          // a task has no id until it is saved.
          key={i}
          task={t}
          onChange={(patch) => update(i, patch)}
          onRemove={() => remove(i)}
        />
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={add}
        className="h-8 text-sm px-2 gap-1.5"
      >
        <Plus className="h-3.5 w-3.5" />
        Add task
      </Button>
    </div>
  );
}

interface TaskEditorProps {
  task: Task;
  onChange: (patch: Partial<Task>) => void;
  onRemove: () => void;
}

function TaskEditor({ task, onChange, onRemove }: TaskEditorProps) {
  return (
    <div className="border rounded-md p-2.5 space-y-2 bg-muted/30">
      <div className="flex items-start gap-1.5">
        <GripVertical className="h-4 w-4 text-muted-foreground mt-2 shrink-0" />
        <Input
          value={task.item}
          onChange={(e) => onChange({ item: e.target.value })}
          placeholder="What needs doing?"
          className="h-8 text-sm px-2 flex-1"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          className="h-8 w-8 shrink-0"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 pl-5">
        <Select
          value={task.status}
          onValueChange={(v) => onChange({ status: v as TaskStatus })}
        >
          <SelectTrigger className="h-8 text-sm w-auto min-w-[110px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} className="text-sm">
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SingleUserField
          value={task.owner}
          editing
          onChange={(owner) => onChange({ owner: owner || undefined })}
        />
        <Input
          type="date"
          value={task.due ? toInputDate(task.due) : ""}
          onChange={(e) => {
            const v = e.target.value;
            onChange({ due: v ? new Date(v).toISOString() : undefined });
          }}
          className="h-8 text-sm px-2 w-auto"
        />
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: TaskStatus }) {
  const cls = cn(
    "text-sm px-2 py-0.5 font-medium uppercase tracking-wide shrink-0 mt-0.5",
    status === "done" && "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20",
    status === "in-progress" && "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
    status === "wont-fix" && "bg-muted text-muted-foreground line-through"
  );
  return (
    <Badge variant="outline" className={cls}>
      {status}
    </Badge>
  );
}

function DueLabel({ due, done }: { due: string; done: boolean }) {
  // The clock is sampled once per mount via a lazy initialiser rather than read
  // during render — `Date.now()` in a render body is impure, so "in 3d" could
  // silently change on an unrelated re-render. The label's finest granularity is
  // a day, so a pinned reading says the same thing for the panel's lifetime.
  const [now] = useState(() => Date.now());
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return null;
  const ms = d.getTime() - now;
  const days = Math.round(ms / (24 * 60 * 60 * 1000));
  const overdue = !done && ms < 0;
  let label: string;
  if (overdue) label = `${Math.abs(days)}d overdue`;
  else if (days === 0) label = "today";
  else if (days === 1) label = "tomorrow";
  else if (days <= 14) label = `in ${days}d`;
  else
    label = d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  return (
    <span
      className={cn(
        "text-sm shrink-0 mt-0.5",
        overdue ? "text-destructive" : "text-muted-foreground"
      )}
    >
      {label}
    </span>
  );
}

function toInputDate(s: string): string {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
}
